import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  FaChevronRight,
  FaFilm,
  FaPlay,
  FaTv,
  FaInfoCircle,
} from "react-icons/fa";

const FEATURED_TITLE = "Top Gear GTA";

export default function Home() {
  const navigate = useNavigate();

  const [banner, setBanner] = useState(null);
  const [shows, setShows] = useState([]);
  const [movies, setMovies] = useState([]);

  const getPublicUrl = useCallback((path) => {
    if (!path) return "";
    const { data } = supabase.storage.from("media").getPublicUrl(path);
    return data?.publicUrl || "";
  }, []);

  const fetchBanner = useCallback(async () => {
    const { data, error } = await supabase
      .from("banners")
      .select("id,message,enabled,variant,link_url,link_text,created_at")
      .eq("enabled", true)
      .order("created_at", { ascending: false })
      .limit(1);

    if (error) {
      console.warn("Banner fetch error:", error.message);
      setBanner(null);
      return;
    }

    setBanner(data?.[0] || null);
  }, []);

  const fetchShows = useCallback(async () => {
    const { data, error } = await supabase
      .from("shows")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      setShows([]);
      return;
    }

    const mapped = (data || []).map((s) => ({
      ...s,
      hero_image_url: s.hero_image_path ? getPublicUrl(s.hero_image_path) : "",
    }));

    setShows(mapped);
  }, [getPublicUrl]);

  const fetchMovies = useCallback(async () => {
    const { data, error } = await supabase
      .from("movies")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.warn("Movies table not found / error:", error.message);
      setMovies([]);
      return;
    }

    const mapped = (data || []).map((m) => ({
      ...m,
      hero_image_url: m.hero_image_path ? getPublicUrl(m.hero_image_path) : "",
    }));

    setMovies(mapped);
  }, [getPublicUrl]);

  useEffect(() => {
    fetchBanner();
    fetchShows();
    fetchMovies();
  }, [fetchBanner, fetchShows, fetchMovies]);

  const featuredItem = useMemo(() => {
    const preferredShow =
      shows.find((show) => show.title === FEATURED_TITLE) || null;

    return preferredShow || shows?.[0] || movies?.[0] || null;
  }, [shows, movies]);

  const isFeaturedShow = useMemo(() => {
    if (!featuredItem) return false;
    return shows.some((item) => item.id === featuredItem.id);
  }, [featuredItem, shows]);

  const bannerStyle = (() => {
    const v = banner?.variant || "info";

    if (v === "warning") {
      return {
        borderColor: "rgba(250,204,21,0.30)",
        backgroundColor: "rgba(234,179,8,0.15)",
      };
    }

    if (v === "success") {
      return {
        borderColor: "rgba(74,222,128,0.30)",
        backgroundColor: "rgba(34,197,94,0.15)",
      };
    }

    if (v === "danger") {
      return {
        borderColor: "rgba(248,113,113,0.30)",
        backgroundColor: "rgba(239,68,68,0.15)",
      };
    }

    return {
      borderColor: "var(--accent-border)",
      backgroundColor: "var(--accent-soft)",
    };
  })();

  const CardRow = ({ items, onClickItem, emptyText, type }) => {
    if (!items.length) {
      return (
        <div
          className="rounded-3xl border border-white/10 p-4 text-sm text-white/70 backdrop-blur-xl sm:p-5 transition-[background-color,border-color] duration-700 ease-in-out"
          style={{ backgroundColor: "rgba(18,9,31,0.72)" }}
        >
          {emptyText}
        </div>
      );
    }

    return (
      <div className="flex gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-5">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onClickItem(item)}
            className="group w-[78vw] max-w-[280px] min-w-[240px] flex-shrink-0 text-left sm:w-[280px]"
          >
            <div
              className="overflow-hidden rounded-[22px] border border-white/10 backdrop-blur-xl transition duration-300 group-hover:scale-[1.02] group-hover:border-white/20 sm:rounded-[24px]"
              style={{
                backgroundColor: "rgba(15,12,24,0.88)",
              }}
            >
              <div className="relative aspect-video w-full overflow-hidden">
                <img
                  src={
                    item.hero_image_url ||
                    "https://via.placeholder.com/640x360?text=Cover"
                  }
                  alt={item.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/20" />
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(143,94,255,0.18),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,59,59,0.16),transparent_32%)]" />

                <div className="absolute left-3 top-3">
                  <div
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white/90 backdrop-blur-md"
                    style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
                  >
                    {type === "movie" ? <FaFilm /> : <FaTv />}
                    <span>{type === "movie" ? "Movie" : "Series"}</span>
                  </div>
                </div>

                <div className="absolute bottom-3 right-3">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-white text-black shadow-2xl transition group-hover:scale-105 sm:h-11 sm:w-11">
                    <FaPlay className="ml-0.5 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </div>
                </div>
              </div>

              <div className="p-3.5 sm:p-4">
                <h3 className="line-clamp-1 text-base font-semibold tracking-tight text-white sm:text-lg">
                  {item.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-xs leading-5 text-white/70 sm:text-sm sm:leading-6">
                  {item.description || "No description available."}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div
      className="min-h-screen text-white transition-[background] duration-700 ease-in-out"
      style={{
        backgroundImage:
          "linear-gradient(to bottom right, var(--shell-from), var(--shell-via), var(--shell-to))",
      }}
    >
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(143,94,255,0.16),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(255,59,59,0.14),transparent_30%)]" />
      </div>

      <div className="relative mx-auto w-full max-w-[1800px] px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        {banner ? (
          <div className="mb-5 sm:mb-6">
            <div
              className="flex w-full flex-col gap-3 rounded-2xl border px-4 py-4 backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between sm:rounded-3xl sm:px-5 transition-[background-color,border-color] duration-700 ease-in-out"
              style={bannerStyle}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 shrink-0 text-white/80">
                  <FaInfoCircle />
                </div>
                <div className="text-sm leading-6 text-white/92">
                  {banner.message}
                </div>
              </div>

              {banner.link_url ? (
                <a
                  href={banner.link_url}
                  className="inline-flex items-center gap-2 self-start rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-md transition hover:bg-white/15 sm:self-auto"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>{banner.link_text || "Learn more"}</span>
                  <FaChevronRight className="h-3 w-3" />
                </a>
              ) : null}
            </div>
          </div>
        ) : null}

        {featuredItem ? (
          <section className="mb-8 sm:mb-10">
            <div
              className="relative overflow-hidden rounded-[24px] border border-white/10 bg-black transition-[box-shadow] duration-700 ease-in-out sm:rounded-[30px]"
              style={{ boxShadow: "var(--frame-shadow)" }}
            >
              <div className="relative min-h-[260px] sm:min-h-[420px] lg:min-h-[520px]">
                <img
                  src={
                    featuredItem.hero_image_url ||
                    "https://via.placeholder.com/1280x720?text=Featured"
                  }
                  alt={featuredItem.title}
                  className="absolute inset-0 h-full w-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/35 sm:via-black/72 sm:to-black/25" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-black/35" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(143,94,255,0.20),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(255,59,59,0.20),transparent_30%)]" />

                <div className="relative z-10 flex min-h-[260px] items-end sm:min-h-[420px] lg:min-h-[520px]">
                  <div className="max-w-3xl px-4 pb-5 sm:px-8 sm:pb-8 lg:px-10 lg:pb-10">
                    <div className="mb-3 flex flex-wrap items-center gap-2 text-[9px] uppercase tracking-[0.2em] text-white/70 sm:text-[11px] sm:tracking-[0.28em]">
                      <span
                        className="rounded-full px-3 py-1 text-white/90 backdrop-blur-md"
                        style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
                      >
                        Light Idea Productions
                      </span>
                      <span>
                        {isFeaturedShow ? "Featured Series" : "Featured Movie"}
                      </span>
                    </div>

                    <h1 className="text-2xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-6xl">
                      {featuredItem.title}
                    </h1>

                    <p className="mt-3 max-w-2xl line-clamp-3 text-sm leading-6 text-white/78 sm:mt-4 sm:line-clamp-none sm:text-base sm:leading-7 lg:text-lg">
                      {featuredItem.description || "No description available."}
                    </p>

                    <div className="mt-5 flex flex-wrap items-center gap-3 sm:mt-6">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            isFeaturedShow
                              ? `/show/${featuredItem.id}`
                              : `/movie/${featuredItem.id}`,
                          )
                        }
                        className="inline-flex items-center gap-3 rounded-full bg-white px-5 py-3 text-sm font-semibold text-black transition hover:scale-[1.02] hover:opacity-95 sm:text-base"
                      >
                        <FaPlay className="ml-0.5" />
                        <span>Play</span>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            isFeaturedShow
                              ? `/show/${featuredItem.id}`
                              : `/movie/${featuredItem.id}`,
                          )
                        }
                        className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/10 px-5 py-3 text-sm font-medium text-white backdrop-blur-md transition hover:bg-white/15 sm:text-base"
                      >
                        <FaInfoCircle />
                        <span>View title</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <section className="mb-8 sm:mb-10">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-[0.24em] text-white/45 sm:text-[11px] sm:tracking-[0.28em]">
                Library
              </div>
              <h2 className="text-xl font-semibold tracking-tight sm:text-3xl">
                Series
              </h2>
            </div>
          </div>

          <CardRow
            items={shows}
            type="series"
            onClickItem={(show) => navigate(`/show/${show.id}`)}
            emptyText="No series yet."
          />
        </section>

        <section className="pb-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-[0.24em] text-white/45 sm:text-[11px] sm:tracking-[0.28em]">
                Library
              </div>
              <h2 className="text-xl font-semibold tracking-tight sm:text-3xl">
                Movies
              </h2>
            </div>
          </div>

          <CardRow
            items={movies}
            type="movie"
            onClickItem={(movie) => navigate(`/movie/${movie.id}`)}
            emptyText="No movies yet."
          />
        </section>
      </div>
    </div>
  );
}
